import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AlertBarComponent } from "./modules/shared/components/alert-bar/alert-bar.component";
import { MessageModalComponent } from "./modules/shared/components/message-modal/message-modal.component";
import { SharedService } from "./services/local/shared/shared.service";
import { Subscription } from "rxjs";
import { ButtonComponent } from "./modules/shared/components/button/button.component";
import { NgxSpinnerModule } from "ngx-spinner";
import { CommonModule } from "@angular/common";
import { LoaderComponent } from "./modules/shared/components/loader/loader.component";
import { CommonService } from './services/remote/common';
import { AlertService } from './services/local/alert/alert.service';
import { AlertType } from './constants/AppConstants';
import { NetworkResponseType } from './services/network/NetworkResponse';
import { isAuthError } from './services/network/Extensions';
import { NavigationService } from './services/local/navigation/navigation.service';
import { AgencyService } from './services/remote/agency/agency.service';
import { LanguageService } from './services/remote/language/language.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, AlertBarComponent, MessageModalComponent, ButtonComponent, NgxSpinnerModule, CommonModule, LoaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'whatsapp-ecommerce';
  refreshButton$: Subscription;
  showRefreshButton = false;
  render = false;

  constructor(
    private agencyService: AgencyService,
    private sharedService: SharedService,
    private commonService: CommonService,
    private alertService: AlertService,
    private navigationService: NavigationService,
    private languageService: LanguageService
  ) {
    this.refreshButton$ = this.sharedService.showRefreshButton.subscribe(show => {
      this.showRefreshButton = show;
    });
  }

  ngOnInit(): void {
    window.parent.postMessage({ type: 'REQUEST_TOKEN' }, '*');
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {

    if (!event?.data?.type) return;

    const { type, encryptedPayload } = event.data;

    if (type === 'TOKEN_RESPONSE') {
      localStorage.setItem('token', encryptedPayload);
      this.decryptEncryptedData().catch(err => {
        console.error('Decrypt call failed:', err);
      });
    }
  }


  ngOnDestroy(): void {
    try {
      this.refreshButton$.unsubscribe();
    } catch (e: any) {
      console.error(e.message);
    }
  }

  onRefreshClick() {

  }

  async decryptEncryptedData() {
    try {
      const apiResponse = await this.commonService.decryptData();

      switch (apiResponse.responseType) {
        case NetworkResponseType.SUCCESS:
          if (apiResponse.response.success) {
            const { customerId, agencyId, language, permanentToken, brand, storeHeading, metaData } = apiResponse.response.data;

            const rawStoreDomain = (metaData as { type?: string, domain?: string })?.domain || '';
            let storeDomain = environment.STORE_BASE_URL;
            if (rawStoreDomain) {
              storeDomain = /^https?:\/\//i.test(rawStoreDomain)
                ? rawStoreDomain
                : `https://${rawStoreDomain}`;

              if (!storeDomain.endsWith('/')) {
                storeDomain = `${storeDomain}/`;
              }
            }

            // Store the decrypted data in local storage
            localStorage.setItem('customerId', customerId);
            localStorage.setItem('agencyId', agencyId);
            localStorage.setItem('language', language);
            localStorage.setItem('permanentToken', permanentToken);
            localStorage.setItem('storeHeading', storeHeading);
            localStorage.setItem('storeDomain', storeDomain);
            sessionStorage.setItem('brand', brand);

            this.fetchAgencyDetails(agencyId);
            this.fetchStaticText();
            return;
          }
          break;

        case NetworkResponseType.API_ERROR:
          // Show Message
          this.alertService.showAlert('Error', apiResponse.response.message, AlertType.ERROR);

          // Check If Authentication Error
          if (isAuthError(apiResponse.code)) {
            // Dismiss Loading
            this.sharedService.dismissLoading();
            // Notify to Navigate
            this.navigationService.logout();
            return;
          }
          break;

        case NetworkResponseType.UNKNOWN_ERROR:
          // Show Message
          this.alertService.showAlert('Error', apiResponse.response.message);
          break;
      }
    } catch (error) {
      console.error('decryptEncryptedData failed:', error);
      this.alertService.showAlert('Error', 'Failed to decrypt data. Please try again later.');
    }
  }

  async fetchAgencyDetails(agencyId: string) {
    try {
      this.agencyService.fetchAgencyDetails(agencyId);
    } catch (_) {
      this.alertService.showAlert('', 'Failed to fetch agency details');
    }
  }

  async fetchStaticText() {
    this.sharedService.showLoading();
    try {
      await this.languageService.fetchStaticText();
    } catch (error) {
      console.error('fetchStaticText failed');
    } finally {
      this.sharedService.dismissLoading();
    }
  }
}
